import React, { useEffect, useState } from "react";
import api from "../api";
import "../css/Notification.css";

import { showSuccessToast, showErrorToast } from "../utils/toastUtils";
import "react-toastify/dist/ReactToastify.css";
import { TOAST_MESSAGES } from "../utils/toastMessages";
import { useDispatch } from "react-redux";
import { fetchUnreadCount } from "../redux/notificationSlice";

const Notifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [filteredNotifications, setFilteredNotifications] = useState([]);
  const [sortOrder, setSortOrder] = useState("desc");
  const [dayFilter, setDayFilter] = useState("");

  const dispatch = useDispatch();
  const token =
    localStorage.getItem("access_token") ||
    sessionStorage.getItem("access_token");

  const authHeader = {
    headers: { Authorization: `Bearer ${token}` },
  };

  /** === Fetch Notifications === **/
  const fetchNotifications = async () => {
    try {
      const res = await api.get(
        `${process.env.REACT_APP_API_URL}/notifications/my`,
        authHeader
      );

      const data = res.data || [];
      setNotifications(data);

      // Apply stored filters if available
      const savedFilters = JSON.parse(localStorage.getItem("notifFilters"));
      if (savedFilters) {
        setSortOrder(savedFilters.sortOrder);
        setDayFilter(savedFilters.dayFilter);
        applyFilters(data, savedFilters.sortOrder, savedFilters.dayFilter);
      } else {
        setFilteredNotifications(data);
      }
    } catch (err) {
      console.error("Error fetching notifications:", err);
    }
  };

  /** === Apply Filters === **/
  const applyFilters = (data, order = sortOrder, day = dayFilter) => {
    let filtered = [...data];

    // Filter by day
    if (day) {
      filtered = filtered.filter((item) => {
        const createdAt = new Date(item.created_timestamp);
        const now = new Date();
        const dayDiff = (now - createdAt) / (1000 * 60 * 60 * 24);

        if (day === "Today")
          return dayDiff < 1 && now.getDate() === createdAt.getDate();
        if (day === "Yesterday")
          return dayDiff < 2 && now.getDate() - createdAt.getDate() === 1;

        return true;
      });
    }

    // Sort by time
    filtered.sort((a, b) => {
      const dateA = new Date(a.created_timestamp);
      const dateB = new Date(b.created_timestamp);
      return order === "asc" ? dateA - dateB : dateB - dateA;
    });

    setFilteredNotifications(filtered);
  };

  /** === Apply Now Button === **/
  const handleApply = () => {
    localStorage.setItem(
      "notifFilters",
      JSON.stringify({ sortOrder, dayFilter })
    );
    applyFilters(notifications);
  };

  /** === Reset Button === **/
  const handleReset = () => {
    setSortOrder("desc");
    setDayFilter("");
    localStorage.removeItem("notifFilters");
    setFilteredNotifications(notifications);
  };

  /** === Mark as Read === **/
  const handleMarkAsRead = async (id) => {
    try {
      await api.put(
        `${process.env.REACT_APP_API_URL}/notifications/read/${id}`,
        {},
        authHeader
      );
      setNotifications((prev) =>
        prev.map((n) =>
          n.notification_id === id ? { ...n, read_status: true } : n
        )
      );
      showSuccessToast(TOAST_MESSAGES.NOTIFICATION.MARK_READ_SUCCESS);
      dispatch(fetchUnreadCount());
      fetchNotifications();
    } catch (err) {
      console.error("Failed to mark as read:", err);
      showErrorToast(TOAST_MESSAGES.NOTIFICATION.MARK_READ_ERROR);
    }
  };

  /** === Delete Notification === **/
  const handleDelete = async (id) => {
    try {
      await api.delete(
        `${process.env.REACT_APP_API_URL}/notifications/${id}`,
        authHeader
      );
      setNotifications((prev) => prev.filter((n) => n.notification_id !== id));
      setFilteredNotifications((prev) =>
        prev.filter((n) => n.notification_id !== id)
      );

      showSuccessToast("Notification deleted successfully.");
      dispatch(fetchUnreadCount());
    } catch (err) {
      console.error("Failed to delete:", err);
      showErrorToast("Failed to delete notification.");
    }
  };

  /** === Fetch on Load === **/
  useEffect(() => {
    fetchNotifications();
  }, []);

  /** === Group Notifications by Day === **/
  const groupedNotifications = filteredNotifications.reduce((acc, item) => {
    const createdAt = new Date(item.created_timestamp);
    const now = new Date();
    const dayDiff = (now - createdAt) / (1000 * 60 * 60 * 24);

    let label = "Earlier";
    if (dayDiff < 1 && now.getDate() === createdAt.getDate()) {
      label = "Today";
    } else if (dayDiff < 2 && now.getDate() - createdAt.getDate() === 1) {
      label = "Yesterday";
    }

    if (!acc[label]) acc[label] = [];
    acc[label].push(item);
    return acc;
  }, {});

  return (
    <div className="p-3 notification-container">
      <div className="mt-2">
        <h5 className="fw-bold refresh-prompts">Notifications</h5>
        <p className="refresh-prompts">
          Stay updated with your latest notifications
        </p>
      </div>

      {/* Header */}
      <div className="d-flex justify-content-between align-items-center border-bottom p-0 mb-2">
        <span className="fw-bold refresh-prompts">
          Notifications ({filteredNotifications.length})
        </span>

        {/* Dropdown */}
        <div className="p-1">
          <button
            className="btn btn-outline-secondary dropdown-toggle px-4"
            type="button"
            data-bs-toggle="dropdown"
            id="modelDropdown"
            aria-expanded="false"
          >
            <i className="bi bi-sort-down me-2"></i> Sort & Filter
          </button>

          <div
            className="dropdown-menu profile-menu-dropdown p-2"
            style={{ minWidth: "220px" }}
          >
            {/* === Sort Section === */}
            <h6 className="fw-bolder refresh-prompts">Sort by Time</h6>
            <hr className="mt-0" />

            {["asc", "desc"].map((order) => (
              <div className="form-check" key={order}>
                <input
                  className="form-check-input"
                  type="radio"
                  name="sortOrder"
                  id={order}
                  value={order}
                  checked={sortOrder === order}
                  onChange={() => setSortOrder(order)}
                />
                <span className="form-check-label">
                  {order === "asc" ? "Ascending" : "Descending"}
                </span>
              </div>
            ))}

            {/* === Filter Section === */}
            <hr />
            <h6 className="fw-bolder refresh-prompts">Filter by Day</h6>
            <hr />

            {["Today", "Yesterday", ""].map((day) => (
              <div className="form-check" key={day || "all"}>
                <input
                  className="form-check-input"
                  type="radio"
                  name="dayFilter"
                  id={day || "all"}
                  value={day}
                  checked={dayFilter === day}
                  onChange={() => setDayFilter(day)}
                />
                <span className="form-check-label">{day || "All"}</span>
              </div>
            ))}

            {/* === Buttons === */}
            <hr />
            <div className="d-flex justify-content-between mt-2">
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={handleReset}
              >
                Reset
              </button>
              <button
                className="btn dropdown-btn bg-dark text-white btn-sm"
                onClick={handleApply}
              >
                Apply Now
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Notification List */}
      {Object.keys(groupedNotifications).map((day) => (
        <div key={day} className="mb-3">
          <h6 className="fw-semibold text-secondary">{day}</h6>
          <div className="list-group list-group-flush">
            {groupedNotifications[day].map((item) => (
              <div
                key={item.notification_id}
                className={`list-group-item my-1 py-2 rounded-3 ${
                  !item.read_status ? "unread" : ""
                }`}
              >
                <div className="notification-message">
                  {item.notification_message}
                </div>

                <div className="notification-meta text-muted">
                  <div className="d-flex align-items-center">
                    <i className="bi bi-calendar me-1"></i>
                    <small>
                      {new Date(item.created_timestamp).toLocaleDateString()}
                    </small>
                  </div>
                  <div className="d-flex align-items-center">
                    <i className="bi bi-clock me-1"></i>
                    <small>
                      {new Date(item.created_timestamp).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </small>
                  </div>
                </div>

                <div className="notification-buttons-mobile">
                  <button
                    title="Mark as read"
                    className="btn btn-sm"
                    onClick={() => handleMarkAsRead(item.notification_id)}
                  >
                    <i className="bi bi-check2-circle"></i>
                  </button>
                  <button
                    title="Delete"
                    className="btn btn-sm"
                    onClick={() => handleDelete(item.notification_id)}
                  >
                    <i className="bi bi-trash"></i>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default Notifications;
